/**
 * ============================================================================================
 * THE SWAP. This is the ONE place the application flow chooses between the fixture and the network.
 * ============================================================================================
 *
 * IT FOLLOWS THE APP'S ONE LIVE FLAG, `isLiveApiConfigured()` in src/config.ts. This file used to
 * carry its own hard-coded `APPLICATION_API_TRANSPORT = 'fixture'` - "flip this one word when
 * `authors-api` is live" - which meant a build pointing the cabinet at the network still sent the
 * application form to the fixture, and flipping it meant editing source rather than setting a
 * build variable. The constant survives, DERIVED from the flag, because the feature's barrel
 * exports it and a screen may want to say which data it is showing.
 *
 * Nothing else in the feature moves: the screens, the hooks and the components all depend on the
 * `ApplicationApi` interface and never on either implementation.
 *
 * WHY IT DEFAULTS TO THE FIXTURE. The default build is the one the accessibility leg checks, and it
 * checks this form with "no server, no Firebase and no session"
 * (`web/ui/test/e2e/application-form-accessibility.spec.ts`), so the fixture is what the
 * shipped markup is measured on.
 *
 * THE SCENARIO OVERRIDE (`?fixture=needs_more_info`) exists so all eight situations — including
 * the ones that need a reviewer's decision to reach — can be opened and looked at without a
 * database. It is read from the URL, so it cannot survive into a production build as state, and
 * it does nothing at all when the transport is `'http'`.
 */
import { AUTHORS_API_BASE_URL, isLiveApiConfigured } from '../../../config';
import { idTokenOrUndefined } from '../../auth/session/access-token';
import { createFixtureApplicationApi } from './fixture';
import type { FixtureScenario } from './fixture';
import { createHttpApplicationApi } from './http';
import type { ApplicationApi } from './types';

export type ApplicationApiTransport = 'fixture' | 'http';

/** The flag, read at call time so a spec can flip it; a build inlines it. */
export function applicationApiTransport(): ApplicationApiTransport {
  return isLiveApiConfigured() ? 'http' : 'fixture';
}

/** What this build chose, for anything that wants to say so. Derived; never edited by hand. */
export const APPLICATION_API_TRANSPORT: ApplicationApiTransport = applicationApiTransport();

const SCENARIOS: readonly FixtureScenario[] = [
  'empty',
  'partial',
  'ready',
  'in_review',
  'needs_more_info',
  'approved',
  'terms_reacceptance',
  // Design 25: a complete profile with the last step, "Our standards and agreement", still to do.
  'complete',
];

/** Reads `?fixture=…` and ignores anything that is not one of the eight. */
export function readFixtureScenario(search: string): FixtureScenario | undefined {
  const asked = new URLSearchParams(search).get('fixture');
  return SCENARIOS.find((s) => s === asked);
}

export interface CreateApplicationApiOptions {
  /** The `'http'` transport's base. Omitted, it is the build's `AUTHORS_API_BASE_URL`. */
  readonly baseUrl?: string;
  /**
   * The `'http'` transport's token getter. Must return a CURRENT token, refreshing near expiry.
   * Omitted, it is the app's one token source - the auth client's current session
   * (`features/auth/session/access-token.ts`), which is exactly that.
   */
  readonly getIdToken?: (forceRefresh?: boolean) => Promise<string | undefined>;
  /** Fixture only. Defaults to `'empty'` — the state the owner named. */
  readonly scenario?: FixtureScenario;
}

export function createApplicationApi(options: CreateApplicationApiOptions = {}): ApplicationApi {
  if (applicationApiTransport() === 'http') {
    // The cabinet is a different origin from the API and carries no cookies, so a request with no
    // bearer token is a 401 and nothing else (adr/0002-edge-addressing.md) - which is why the token
    // getter is defaulted to the one source rather than left out.
    return createHttpApplicationApi({
      baseUrl: options.baseUrl ?? AUTHORS_API_BASE_URL,
      getIdToken: options.getIdToken ?? idTokenOrUndefined(),
    });
  }
  return createFixtureApplicationApi({
    ...(options.scenario === undefined ? {} : { scenario: options.scenario }),
  });
}

/**
 * Leg one of both uploads, whose swap now lives with the uploader rather than here:
 * ../../storage/create-quarantine-uploader.ts follows the same one live flag, and in a live build
 * hands back the real `firebase/storage` writer, loaded at the first write. This used to return the
 * fixture unconditionally, so a live build's claim named an object that was never written and the
 * API refused it. Re-exported under the same name, so the feature's barrel and `/apply` are
 * unchanged - and the editor, which cannot reach this feature, imports the same switch directly.
 */
export { createQuarantineUploader } from '../../storage/create-quarantine-uploader';
